import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Zap, PlayCircle, Clock, CheckCircle, XCircle, AlertCircle,
  Users, Globe, Lock, Calendar, Edit2, Copy, Settings, History, Pause,
  Send, MoreHorizontal, Activity, RefreshCw, AlertTriangle, Loader2,
  ChevronRight, ExternalLink, FileText, Code, Terminal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const mockWorkflow = {
  id: 'wf-1',
  name: 'Password Reset',
  description: 'Self-service password reset with identity verification. Guides users through MFA verification, resets their password, and notifies them via email.',
  type: 'helpdesk',
  scope: 'org_wide',
  status: 'published',
  version: 'v2.1',
  team: 'IT Operations',
  owner: 'Emily Zhang',
  createdBy: 'Emily Zhang',
  createdAt: '2023-11-15',
  lastModified: '2024-01-10',
  triggers: [
    { type: 'keyword', value: 'password reset, forgot password, change password' },
    { type: 'classification', value: 'Password / Authentication Issues' }
  ],
  actions: [
    { id: 'step-1', name: 'Verify Identity', type: 'mfa_challenge', system: 'Okta', description: 'Send MFA push to registered device' },
    { id: 'step-2', name: 'Check Account Status', type: 'api_call', system: 'Okta', description: 'Verify account is not locked or suspended' },
    { id: 'step-3', name: 'Generate Reset Link', type: 'api_call', system: 'Okta', description: 'Create time-limited password reset URL' },
    { id: 'step-4', name: 'Send Notification', type: 'notification', system: 'Email', description: 'Email reset link to user' }
  ],
  stats: {
    runCount7d: 234,
    runCountTotal: 4521,
    successRate: 98.2,
    avgDuration: '2m 15s',
    lastRun: '5 min ago'
  },
  runHistory: [
    { id: 'run-001', status: 'success', duration: '2m 10s', timestamp: '2024-01-15T10:30:00Z', triggeredBy: 'Sarah Chen', ticket: 'TKT-1234' },
    { id: 'run-002', status: 'success', duration: '1m 58s', timestamp: '2024-01-15T09:15:00Z', triggeredBy: 'Mike Torres', ticket: 'TKT-1228' },
    { id: 'run-003', status: 'failed', duration: '45s', timestamp: '2024-01-15T08:00:00Z', triggeredBy: 'Alex Kim', ticket: 'TKT-1220', error: 'MFA verification timeout' },
    { id: 'run-004', status: 'success', duration: '2m 22s', timestamp: '2024-01-14T16:45:00Z', triggeredBy: 'Jordan Lee', ticket: 'TKT-1215' },
    { id: 'run-005', status: 'success', duration: '2m 05s', timestamp: '2024-01-14T14:30:00Z', triggeredBy: 'Taylor Brown', ticket: 'TKT-1210' }
  ],
  versionHistory: [
    { version: 'v2.1', date: '2024-01-10', author: 'Emily Zhang', changes: 'Added retry logic for MFA timeout' },
    { version: 'v2.0', date: '2023-12-20', author: 'Emily Zhang', changes: 'Added MFA verification step' },
    { version: 'v1.5', date: '2023-12-01', author: 'David Park', changes: 'Improved email template' },
    { version: 'v1.0', date: '2023-11-15', author: 'Emily Zhang', changes: 'Initial release' }
  ]
};

const statusConfig = {
  published: { label: 'Published', color: 'bg-green-100 text-green-800 border-green-200' },
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  testing: { label: 'Testing', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-800 border-red-200' }
};

const scopeConfig = {
  org_wide: { label: 'Org-wide', icon: Globe, color: 'text-blue-600' },
  team_only: { label: 'Team Only', icon: Lock, color: 'text-amber-600' }
};

export const WorkflowDetail = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname.includes('/runs') ? 'runs' : 'overview');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [testInput, setTestInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  const [testStatus, setTestStatus] = useState(null);
  const [publishScope, setPublishScope] = useState('team_only');

  const workflow = mockWorkflow;
  const status = statusConfig[workflow.status];
  const scope = scopeConfig[workflow.scope];
  const ScopeIcon = scope.icon;

  const handleTestRun = async () => {
    setIsRunning(true);
    setTestProgress(0);
    setTestStatus('running');

    for (let i = 0; i <= 100; i += 25) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setTestProgress(i);
    }

    setIsRunning(false);
    setTestStatus('success');
    toast.success('Test run completed', {
      description: 'All steps executed successfully in dry-run mode.'
    });
  };

  const handlePublish = () => {
    setShowPublishDialog(false);
    toast.success('Workflow submitted for approval', {
      description: 'A manager will review before publishing.'
    });
    navigate('/workflow-library');
  };

  const handleDisable = () => {
    setShowDisableDialog(false);
    toast.success('Workflow disabled', {
      description: 'No new runs will be triggered.'
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        navigate('/workflow-library');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/workflow-library" aria-label="Back to workflow library">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              </Button>
            </Link>
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-primary" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-foreground">{workflow.name}</h1>
                <Badge variant="outline" className="text-xs">{workflow.version}</Badge>
                <Badge variant="outline" className={cn("text-xs", status.color)}>
                  {status.label}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <ScopeIcon className={cn("w-3 h-3 mr-1", scope.color)} aria-hidden="true" />
                  {scope.label}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{workflow.team} &middot; {workflow.owner}</p>
            </div>
            <div className="flex items-center gap-2">
              {workflow.status === 'published' && (
                <Button variant="outline" onClick={() => setShowDisableDialog(true)}>
                  <Pause className="w-4 h-4 mr-2" aria-hidden="true" />
                  Disable
                </Button>
              )}
              {(workflow.status === 'draft' || workflow.status === 'testing') && (
                <>
                  <Button variant="outline" onClick={() => setShowTestDialog(true)}>
                    <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                    Test Run
                  </Button>
                  <Button onClick={() => setShowPublishDialog(true)}>
                    <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                    Publish
                  </Button>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Edit2 className="w-4 h-4 mr-2" aria-hidden="true" />
                    Edit Workflow
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Copy className="w-4 h-4 mr-2" aria-hidden="true" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <History className="w-4 h-4 mr-2" aria-hidden="true" />
                    Version History
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="w-4 h-4 mr-2" aria-hidden="true" />
                    Settings
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="runs">
              Run History
              <Badge variant="secondary" className="ml-2 text-xs">{workflow.stats.runCount7d}</Badge>
            </TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8 space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{workflow.description}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Triggers</CardTitle>
                    <CardDescription>Conditions that start this workflow</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {workflow.triggers.map((trigger, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <Badge variant="secondary" className="text-xs shrink-0">{trigger.type}</Badge>
                        <span className="text-sm text-foreground">{trigger.value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Workflow Steps</CardTitle>
                    <CardDescription>{workflow.actions.length} actions in sequence</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {workflow.actions.map((action, idx) => (
                        <div
                          key={action.id}
                          className="flex items-start gap-4 p-4 border rounded-lg"
                          role="listitem"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-medium text-foreground">{action.name}</h4>
                              <Badge variant="outline" className="text-xs">{action.system}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{action.description}</p>
                          </div>
                          {idx < workflow.actions.length - 1 && (
                            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-2" aria-hidden="true" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="col-span-4 space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Performance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Runs (7 days)</span>
                      <span className="font-medium text-foreground">{workflow.stats.runCount7d}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total runs</span>
                      <span className="font-medium text-foreground">{workflow.stats.runCountTotal.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Success rate</span>
                      <span className="font-medium text-green-600">{workflow.stats.successRate}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avg duration</span>
                      <span className="font-medium text-foreground">{workflow.stats.avgDuration}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Last run</span>
                      <span className="font-medium text-foreground">{workflow.stats.lastRun}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created by</span>
                      <span className="text-foreground">{workflow.createdBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created</span>
                      <span className="text-foreground">{workflow.createdAt}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last modified</span>
                      <span className="text-foreground">{workflow.lastModified}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Team</span>
                      <span className="text-foreground">{workflow.team}</span>
                    </div>
                  </CardContent>
                </Card>

                {workflow.status === 'published' && (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-900">Live & Active</p>
                          <p className="text-xs text-green-700 mt-1">
                            This workflow is available for production use.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="runs" className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Recent Runs</CardTitle>
                  <Button variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full" role="table" aria-label="Run history">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Run ID</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Duration</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Triggered By</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Ticket</th>
                      <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Time</th>
                      <th className="w-12 p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workflow.runHistory.map((run) => (
                      <tr key={run.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-4">
                          <span className="text-sm font-mono text-muted-foreground">{run.id}</span>
                        </td>
                        <td className="p-4">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              run.status === 'success' && "bg-green-100 text-green-800 border-green-200",
                              run.status === 'failed' && "bg-red-100 text-red-800 border-red-200"
                            )}
                          >
                            {run.status === 'success' ? (
                              <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                            ) : (
                              <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                            )}
                            {run.status}
                          </Badge>
                          {run.error && (
                            <p className="text-xs text-red-600 mt-1">{run.error}</p>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{run.duration}</td>
                        <td className="p-4 text-sm text-foreground">{run.triggeredBy}</td>
                        <td className="p-4">
                          {run.ticket && (
                            <Link
                              to={`/tickets/${run.ticket}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {run.ticket}
                            </Link>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(run.timestamp).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <Button variant="ghost" size="sm" aria-label="View run details">
                            <FileText className="w-4 h-4" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="versions" className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Version History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workflow.versionHistory.map((ver, idx) => (
                    <div
                      key={ver.version}
                      className={cn(
                        "flex items-start gap-4 p-4 rounded-lg border",
                        idx === 0 && "bg-primary/5 border-primary/20"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                        idx === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                      )}>
                        <Code className="w-4 h-4" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{ver.version}</span>
                          {idx === 0 && (
                            <Badge variant="default" className="text-xs">Current</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{ver.changes}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {ver.author} &middot; {ver.date}
                        </p>
                      </div>
                      {idx > 0 && (
                        <Button variant="ghost" size="sm">
                          <History className="w-4 h-4 mr-2" aria-hidden="true" />
                          Restore
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-primary" />
              Test Run: {workflow.name}
            </DialogTitle>
            <DialogDescription>
              Run this workflow in test mode. No changes will be made to production systems.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {testStatus === null && (
              <>
                <Alert>
                  <Terminal className="h-4 w-4" />
                  <AlertDescription>
                    Test mode simulates all API calls without making actual changes.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label htmlFor="test-input">Test Input (Optional)</Label>
                  <Input
                    id="test-input"
                    placeholder="e.g., user email or ticket ID"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                  />
                </div>
              </>
            )}

            {testStatus === 'running' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  <span className="text-sm font-medium">Running test...</span>
                </div>
                <Progress value={testProgress} className="h-2" />
                <div className="space-y-2">
                  {workflow.actions.map((action, idx) => {
                    const stepProgress = (testProgress / 100) * workflow.actions.length;
                    const isComplete = idx < stepProgress;
                    const isCurrent = idx === Math.floor(stepProgress);

                    return (
                      <div
                        key={action.id}
                        className={cn(
                          "flex items-center gap-2 text-sm p-2 rounded",
                          isComplete && "text-green-600",
                          isCurrent && "bg-primary/10 text-primary",
                          !isComplete && !isCurrent && "text-muted-foreground"
                        )}
                      >
                        {isComplete ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isCurrent ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                        {action.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {testStatus === 'success' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Test completed successfully</span>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    All {workflow.actions.length} steps executed in dry-run mode. No production changes were made.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {testStatus === null && (
              <>
                <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
                <Button onClick={handleTestRun} disabled={isRunning}>
                  <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                  Start Test Run
                </Button>
              </>
            )}
            {testStatus === 'success' && (
              <Button onClick={() => { setShowTestDialog(false); setTestStatus(null); setTestProgress(0); }}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Publish: {workflow.name}
            </DialogTitle>
            <DialogDescription>
              Submit this workflow for review and publishing.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Publish Scope</Label>
              <Select value={publishScope} onValueChange={setPublishScope}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_only">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" aria-hidden="true" />
                      Team Only (Recommended for new workflows)
                    </div>
                  </SelectItem>
                  <SelectItem value="org_wide">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" aria-hidden="true" />
                      Organization-wide
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Publishing requires approval from a team manager. The workflow will be submitted for review.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>Cancel</Button>
            <Button onClick={handlePublish}>
              <Send className="w-4 h-4 mr-2" aria-hidden="true" />
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5 text-amber-500" />
              Disable Workflow
            </DialogTitle>
            <DialogDescription>
              Temporarily disable this workflow. No new runs will be triggered.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                In-progress runs will complete, but no new triggers will be processed.
                You can re-enable at any time.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDisable}>
              <Pause className="w-4 h-4 mr-2" aria-hidden="true" />
              Disable Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowDetail;
